import type { Form, FormField } from "@repo/app-types/entities/form";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const formApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Form hooks
    allForms: builder.query<Form[], void>({
      query: () => "/forms",
      providesTags: (result) => getTagsProvidedByList(TagType.Form, result),
    }),
    createForm: builder.mutation<
      Form,
      {
        name: string;
        settings: {
          fields: readonly FormField[];
        };
      }
    >({
      query: (values) => ({
        method: "POST",
        url: "/forms",
        body: values,
      }),
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.Form),
    }),
    updateForm: builder.mutation<
      Form,
      {
        id: string;
        name?: string;
        settings?: {
          fields?: readonly FormField[];
        };
      }
    >({
      query: (values) => ({
        method: "PATCH",
        url: `/forms/${encodeURIComponent(values.id)}`,
        body: values,
      }),
      invalidatesTags: (result, error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.Form, id),
    }),
    deleteForm: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/forms/${encodeURIComponent(id)}`,
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          formApi.util.updateQueryData("allForms", undefined, (draft) => {
            const index = draft.findIndex((template) => template.id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.Form, id),
    }),
  }),
});

export const {
  useAllFormsQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
} = formApi;
